import { defineStore } from 'pinia'
import { OnboardingStep, StepResponse, Step } from './onboardingStep.d'
import { ref, Ref } from 'vue'
import { AxiosResponse } from 'axios'
import createHttpClient from '@/api/httpClient'
import MessageService from '@/components/feedback/message/messageService'
import { useUserStore } from '@/store/user/userStore'
import { useStorage } from '@vueuse/core'

const httpClient = createHttpClient()

export const useOnboardingStore = defineStore('onboarding', () => {
  const step: Ref<Step> = ref(null)
  const hasOnboarding = ref<boolean>()
  const message = ref<string>('')
  const nextLoading = ref(false)
  const prevLoading = ref(false)
  const loading = ref(false)
  const onboardingAlert = ref(false)
  const visible = ref(true)
  const userStore = useUserStore()
  const manualProccess = ref(false)
  const noLongerDisplay = ref(false)
  const continueLater = useStorage('continue-later', false)
  const leave = ref(false)
  // get current user step
  const currentStep = async () => {
    if (loading.value || userStore.user.isActive) return
    try {
      loading.value = true

      const { data }: AxiosResponse<StepResponse> =
        await httpClient.get<StepResponse>('/api/onboarding/current-step')

      step.value = data.step
      hasOnboarding.value = data.hasOnboarding
      message.value = data.message
    } catch (err) {
      MessageService.error('')
    } finally {
      loading.value = false
      manualProccess.value = false
    }
  }

  const nextStep = async () => {
    if (nextLoading.value || prevLoading.value) return
    try {
      manualProccess.value = true
      nextLoading.value = true
      const { data }: AxiosResponse<StepResponse> =
        await httpClient.post<StepResponse>('/api/onboarding/next-step')

      visible.value = false
      if (step.value.targetHtmlElement !== 'submit-account-validation-button') {
        setTimeout(() => (visible.value = true), 300)
      }

      step.value = data.step
      hasOnboarding.value = data.hasOnboarding
      message.value = data.message
      return true
    } catch (err) {
      MessageService.error('')
    } finally {
      nextLoading.value = false
    }
  }

  const previousStep = async () => {
    if (prevLoading.value || nextLoading.value) return
    try {
      manualProccess.value = true
      prevLoading.value = true
      const { data }: AxiosResponse<StepResponse> =
        await httpClient.post<StepResponse>('/api/onboarding/previous-step')
      step.value = data.step
      hasOnboarding.value = data.hasOnboarding
      message.value = data.message

      visible.value = false
      setTimeout(() => (visible.value = true), 300)
    } catch (err) {
      MessageService.error('')
    } finally {
      prevLoading.value = false
    }
  }

  const toggleOnboarding = async (value: boolean) => {
    if (loading.value) return
    try {
      loading.value = true

      const { data }: AxiosResponse<StepResponse> =
        await httpClient.put<StepResponse>(
          `/api/onboarding/toggle-visibility?isHidden=${value}`,
        )
      step.value = data.step
      hasOnboarding.value = data.hasOnboarding
      message.value = data.message

      if (!value) {
        visible.value = true
      }
      return true
    } catch (err) {
      MessageService.error('')
    } finally {
      loading.value = false
      onboardingAlert.value = false
    }
  }

  const resetOnboarding = async () => {
    if (loading.value) return
    try {
      loading.value = true
      const { data }: AxiosResponse<StepResponse> =
        await httpClient.post<StepResponse>('/api/onboarding/reset')
      step.value = data.step
      hasOnboarding.value = data.hasOnboarding
      message.value = data.message

      return true
    } catch (err) {
      MessageService.error('')
    } finally {
      loading.value = false
    }
  }

  return {
    step,
    hasOnboarding,
    message,
    nextLoading,
    prevLoading,
    loading,
    onboardingAlert,
    visible,
    currentStep,
    nextStep,
    previousStep,
    resetOnboarding,
    toggleOnboarding,
    continueLater,
    manualProccess,
    leave,
  }
})
